import React, { useCallback, useEffect } from 'react'
import { Modal } from './Modal'

import '../assets/css/style.css'
import info from '../assets/img/icon/info.svg'

interface SimulationGuideModalProps {
  isShow: boolean
  setShow: (isShow: boolean) => void
}

export const SimulationGuideModal: React.FC<SimulationGuideModalProps> = React.memo((props) => {
  const { isShow, setShow } = props
  useEffect(() => {
    if (isShow) {
      document.body.setAttribute('style', 'overflow: hidden;')
    } else {
      document.body.removeAttribute('style')
    }

    return () => document.body.removeAttribute('style')
  }, [isShow])

  const handleHide = useCallback(() => {
    setShow(false)
  }, [setShow])

  return (
    <React.Fragment>
      {isShow && (
        <Modal className={'c-modal'}>
          <div className="c-modal__content">
            <div className="c-modal__head">
              <span className="c-modal__head__icon">
                <img src={info} alt="" />
              </span>
              <h2 className="c-modal__head__title">
                将来シミュレーション
                <br className="spOnly" /> 使い方ガイド
              </h2>
              <span className="c-modal__head__close" onClick={handleHide}></span>
            </div>
            <div className="c-modal__body">
              <div className="c-modal__lead">
                <p>
                  あなたの資産の将来をシミュレーションできます。
                  <br />
                  シミュレーションは、評価額の低い順から7ケースが表示されます。
                </p>
              </div>
              <h3 className="c-heading-1">操作方法</h3>
              <p className="u-mb1em">
                {/* eslint-disable-next-line no-irregular-whitespace */}
                STEP１　お客さまが想定されるリターン（年率）、リスク（年率）を入力して下さい。
                <br />
                初期投資額、積立額(毎月)、期間を変更することができます。
              </p>
              <p className="u-mb2em">
                {/* eslint-disable-next-line max-len */}
                ※“つみたて”プラン（積立投資）において、お客さまが想定されるリターン（年率）、リスク（年率）が異なる場合は、「“つみたて”プラン（積立投資）のリターン（年率）、リスク（年率）を個別に設定する」をクリック（タップ）し、想定されるリターン（年率）、リスク（年率）を入力してください。また、“つみたて”プラン（積立投資）のリターン（年率）、リスク（年率）を個別に設定しない場合は、初期投資額と同じリターン（年率）、リスク（年率）を用いて計算します。
              </p>
              <p className="u-mb1em">
                {/* eslint-disable-next-line no-irregular-whitespace */}
                STEP２　「計算」をクリック（タップ）してください。
              </p>
              <p className="u-mb1em">
                各項目の入力数値は以下の通りとなります。
                <br />
                入力値が下記表の範囲外の場合、エラーが表示されます。範囲内の数値を入力してください。
              </p>
              <table className="c-table">
                <tbody>
                  <tr>
                    <th colSpan={2}>初期投資入力範囲</th>
                  </tr>
                  <tr>
                    <th>
                      初期投資額<sup>*</sup>
                    </th>
                    <td>0〜99999.9 万円</td>
                  </tr>
                  <tr>
                    <th>
                      リターン（年率）<sup>*</sup>
                    </th>
                    <td>0〜99.9 ％</td>
                  </tr>
                  <tr>
                    <th>
                      リスク（年率）<sup>*</sup>
                    </th>
                    <td>0〜99.9 ％</td>
                  </tr>
                </tbody>
              </table>
              <p className="u-mb1em">* 小数点一桁まで有効</p>
              <table className="c-table">
                <tbody>
                  <tr>
                    <th colSpan={2}>積立投資入力範囲</th>
                  </tr>
                  <tr>
                    <th>
                      積立額（毎月）<sup>*</sup>
                    </th>
                    <td>0〜999.9 万円</td>
                  </tr>
                  <tr>
                    <th>
                      リターン（年率）<sup>*</sup>
                    </th>
                    <td>0〜99.9 ％</td>
                  </tr>
                  <tr>
                    <th>
                      リスク（年率）<sup>*</sup>
                    </th>
                    <td>0〜99.9 ％</td>
                  </tr>
                </tbody>
              </table>
              <p className="u-mb1em">* 小数点一桁まで有効</p>
              <table className="c-table">
                <tbody>
                  <tr>
                    <th colSpan={2}>預金金利入力範囲</th>
                  </tr>
                  <tr>
                    <th>
                      預金金利（年率）<sup>*</sup>
                    </th>
                    <td>0〜99.99 ％</td>
                  </tr>
                </tbody>
              </table>
              <p className="u-mb1em">* 小数点二桁まで有効</p>
              <table className="c-table">
                <tbody>
                  <tr>
                    <th colSpan={2}>期間入力範囲</th>
                  </tr>
                  <tr>
                    <th>期間</th>
                    <td>1〜40 年</td>
                  </tr>
                </tbody>
              </table>
              <p>評価額の上限は、999,999万円となります。</p>

              <h3 className="c-heading-1">シミュレーションの計算方法</h3>
              <p>モンテカルロ法を用いて計算しています。</p>
              <p>サンプル数は10,000としています。</p>
              <p>
                評価額（シミュレーション結果）は対数正規分布に従うとの前提で、サンプル数および評価額を求め、表示しています。
              </p>

              <h3 className="c-heading-1">用語説明</h3>
              <h4>参考情報</h4>
              <dl className="c-modal__d-list">
                <dt>リターン</dt>
                <dd>
                  リターンとは、投資収益率のことで、3年、5年など、決められた2つの時点の価格（指数値）を比較して、何％上昇したか（あるいは下落したか）を表す指標のことです。
                </dd>
              </dl>
              <dl className="c-modal__d-list">
                <dt>リスク</dt>
                <dd>
                  {/* eslint-disable-next-line max-len */}
                  リスクとは、リターン（投資収益率）のぶれを意味します。高いリターンを求めるのであれば高いリスクを許容する必要があり、リスクをとりたくないのであれば低いリターンを享受すべきであるとされます。
                </dd>
              </dl>
            </div>
          </div>
          <div className="c-modal__overlay" onClick={handleHide}></div>
        </Modal>
      )}
    </React.Fragment>
  )
})
